import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Intercepteur pour ajouter le token JWT
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Intercepteur pour gérer les erreurs
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (data) => api.post('/auth/register', data),
  getCurrentUser: () => api.get('/auth/me'),
};

// Dossiers
export const dossiersAPI = {
  getAll: (params) => api.get('/dossiers', { params }),
  getById: (id) => api.get(`/dossiers/${id}`),
  create: (data) => api.post('/dossiers', data),
  update: (id, data) => api.patch(`/dossiers/${id}`, data),
  delete: (id) => api.delete(`/dossiers/${id}`),
  // Archivage
  archive: (id) => api.post(`/dossiers/${id}/archive`),
  unarchive: (id) => api.post(`/dossiers/${id}/unarchive`),
  // Corbeille
  softDelete: (id) => api.post(`/dossiers/${id}/soft-delete`),
  restore: (id) => api.post(`/dossiers/${id}/restore`),
  permanentDelete: (id) => api.delete(`/dossiers/${id}/permanent`),
  getTrashStats: () => api.get('/dossiers/stats/trash'),
};

// Gestionnaires
export const gestionnairesAPI = {
  getAll: () => api.get('/gestionnaires'),
  getById: (id) => api.get(`/gestionnaires/${id}`),
  create: (data) => api.post('/gestionnaires', data),
  update: (id, data) => api.patch(`/gestionnaires/${id}`, data),
  delete: (id) => api.delete(`/gestionnaires/${id}`),
};

// Tables
export const tablesAPI = {
  getCaisses: () => api.get('/tables/caisses'),
  createCaisse: (data) => api.post('/tables/caisses', data),
  updateCaisse: (id, data) => api.patch(`/tables/caisses/${id}`, data),
  deleteCaisse: (id) => api.delete(`/tables/caisses/${id}`),
  getDepartements: (params) => api.get('/tables/departements', { params }),
  updateDepartement: (id, data) => api.patch(`/tables/departements/${id}`, data),
};

// Dashboard
export const dashboardAPI = {
  getStats: (gestionnaireId) => api.get('/dashboard/stats', { params: { gestionnaire_id: gestionnaireId } }),
  getTeamLoad: () => api.get('/dashboard/gestionnaires-load'),
  getRecentActivity: (limit = 10) => api.get('/dashboard/recent-activity', { params: { limit } }),
};

// AI Assistant
export const aiAPI = {
  chat: (data) => api.post('/ai/chat', data),
  getConversation: (dossierId) => api.get(`/ai/conversation/${dossierId}`),
  exportPDF: (dossierId) => api.get(`/ai/export-pdf/${dossierId}`, { responseType: 'blob' }),
};

// Widgets
export const widgetsAPI = {
  getAll: () => api.get('/widgets'),
  getById: (id) => api.get(`/widgets/${id}`),
  getByCode: (code) => api.get(`/widgets/code/${code}`),
  create: (data) => api.post('/widgets', data),
  update: (id, data) => api.patch(`/widgets/${id}`, data),
  toggle: (id) => api.patch(`/widgets/${id}/toggle`),
  delete: (id) => api.delete(`/widgets/${id}`),
};

// AGIRC-ARRCO
export const agircAPI = {
  rechercheCaisse: (data) => api.post('/agirc/recherche-caisse', data),
  getStatus: () => api.get('/agirc/status'),
};

// AMO (Assistants à Maîtrise d'Ouvrage)
export const amoAPI = {
  getAll: (includeHidden = false) => api.get(`/amo?include_hidden=${includeHidden}`),
  getById: (id) => api.get(`/amo/${id}`),
  create: (data) => api.post('/amo', data),
  update: (id, data) => api.patch(`/amo/${id}`, data),
  delete: (id) => api.delete(`/amo/${id}`),
  reorder: (order) => api.post('/amo/reorder', order),
};

// Configuration du tableau (colonnes et filtres)
export const tableauConfigAPI = {
  // Colonnes
  getColonnesConfig: () => api.get('/tableau-config/colonnes'),
  getColonnesDisponibles: () => api.get('/tableau-config/colonnes/disponibles'),
  updateColonnesConfig: (colonnes) => api.patch('/tableau-config/colonnes', colonnes),
  resetColonnesConfig: () => api.post('/tableau-config/colonnes/reset'),
  
  // Filtres personnalisés
  getFiltres: () => api.get('/tableau-config/filtres'),
  getFiltre: (id) => api.get(`/tableau-config/filtres/${id}`),
  createFiltre: (data) => api.post('/tableau-config/filtres', data),
  updateFiltre: (id, data) => api.patch(`/tableau-config/filtres/${id}`, data),
  deleteFiltre: (id) => api.delete(`/tableau-config/filtres/${id}`),
  
  // Options de filtres (valeurs dynamiques)
  getOptionsCaisses: () => api.get('/tableau-config/filtres/options/caisses'),
  getOptionsInstallateurs: () => api.get('/tableau-config/filtres/options/installateurs'),
  getOptionsAmos: () => api.get('/tableau-config/filtres/options/amos'),
  getOptionsStatuts: () => api.get('/tableau-config/filtres/options/statuts'),
  getOptionsStatutsTravaux: () => api.get('/tableau-config/filtres/options/statuts-travaux'),
  getOptionsStatutsAnah: () => api.get('/tableau-config/filtres/options/statuts-anah'),
};

// Configuration
export const configAPI = {
  getStatuts: (includeInactive = false) => api.get(`/config/statuts?include_inactive=${includeInactive}`),
  getStatut: (id) => api.get(`/config/statuts/${id}`),
  createStatut: (data) => api.post('/config/statuts', data),
  updateStatut: (id, data) => api.patch(`/config/statuts/${id}`, data),
  deleteStatut: (id) => api.delete(`/config/statuts/${id}`),
  toggleStatut: (id) => api.patch(`/config/statuts/${id}/toggle`),
  reorderStatuts: (order) => api.post('/config/statuts/reorder', order),
  // Logs API
  getApiLogs: (params) => api.get('/config/api-logs', { params }),
  getApiLogsStats: () => api.get('/config/api-logs/stats'),
};

// Documents
export const documentsAPI = {
  getTypes: () => api.get('/documents/types'),
  getByDossier: (dossierId) => api.get(`/documents/dossier/${dossierId}`),
  upload: (formData) => api.post('/documents/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  download: (documentId) => api.get(`/documents/download/${documentId}`, { responseType: 'blob' }),
  delete: (documentId, deletedBy, deletedByNom) => 
    api.delete(`/documents/${documentId}`, { 
      params: { deleted_by: deletedBy, deleted_by_nom: deletedByNom } 
    }),
  getStats: (dossierId) => api.get(`/documents/stats/${dossierId}`),
};

// Checklist
export const checklistAPI = {
  // Types de documents
  getDocumentTypes: (includeInactive = false) => api.get(`/checklist/document-types?include_inactive=${includeInactive}`),
  createDocumentType: (data) => api.post('/checklist/document-types', data),
  updateDocumentType: (id, data) => api.patch(`/checklist/document-types/${id}`, data),
  deleteDocumentType: (id) => api.delete(`/checklist/document-types/${id}`),
  
  // Templates
  getTemplates: () => api.get('/checklist/templates'),
  createTemplate: (data) => api.post('/checklist/templates', data),
  
  // Checklist par dossier
  getByDossier: (dossierId) => api.get(`/checklist/dossier/${dossierId}`),
  initForDossier: (dossierId, typeDemande) => api.post(`/checklist/dossier/${dossierId}/init?type_demande=${typeDemande || 'standard'}`),
  syncForDossier: (dossierId) => api.post(`/checklist/dossier/${dossierId}/sync`),
  syncAllDossiers: () => api.post('/checklist/sync-all'),
  addItem: (dossierId, data) => api.post(`/checklist/dossier/${dossierId}/item`, null, { params: data }),
  updateItem: (itemId, data, userId, userNom) => api.patch(`/checklist/item/${itemId}`, data, { params: { user_id: userId, user_nom: userNom } }),
  deleteItem: (itemId, userId, userNom) => api.delete(`/checklist/item/${itemId}`, { params: { user_id: userId, user_nom: userNom } }),
  
  // Stats et alertes
  getStats: (dossierId) => api.get(`/checklist/dossier/${dossierId}/stats`),
  getAlertes: (dossierId) => api.get(`/checklist/dossier/${dossierId}/alertes`),
  
  // Init
  initDefaults: () => api.post('/checklist/init-defaults'),
};

// Suivi / Historique
export const suiviAPI = {
  getSuivis: (dossierId) => api.get(`/suivi/${dossierId}/suivis`),
  create: (data) => api.post('/suivi', data),
  createSuivi: (data) => api.post('/suivi', data),
  getCommentaires: (dossierId) => api.get(`/suivi/commentaires/${dossierId}`),
  createCommentaire: (data) => api.post('/suivi/commentaires', data),
  deleteCommentaire: (commentaireId) => api.delete(`/suivi/commentaires/${commentaireId}`),
  addCommentaire: (data) => api.post('/suivi/commentaire', data),
};

// Configuration Fiche Dossier
export const dossierConfigAPI = {
  // Sections
  getSections: (includeHidden = false) => api.get(`/dossier-config/sections?include_hidden=${includeHidden}`),
  getAllSections: () => api.get('/dossier-config/sections/all'),
  createSection: (data) => api.post('/dossier-config/sections', data),
  updateSection: (id, data) => api.patch(`/dossier-config/sections/${id}`, data),
  deleteSection: (id) => api.delete(`/dossier-config/sections/${id}`),
  reorderSections: (ids) => api.post('/dossier-config/sections/reorder', ids),
  
  // Champs
  getFields: (sectionCode = null, includeHidden = false) => {
    let url = `/dossier-config/fields?include_hidden=${includeHidden}`;
    if (sectionCode) url += `&section_code=${sectionCode}`;
    return api.get(url);
  },
  getAllFields: () => api.get('/dossier-config/fields/all'),
  createField: (data) => api.post('/dossier-config/fields', data),
  updateField: (id, data) => api.patch(`/dossier-config/fields/${id}`, data),
  deleteField: (id) => api.delete(`/dossier-config/fields/${id}`),
  reorderFields: (ids) => api.post('/dossier-config/fields/reorder', ids),
  
  // Config complète
  getFullConfig: () => api.get('/dossier-config/full'),
  getFullConfigAdmin: () => api.get('/dossier-config/full/admin'),
  
  // Config onglet Documents
  getDocumentsPageConfig: () => api.get('/dossier-config/documents-page'),
  
  // Init
  initDefaults: () => api.post('/dossier-config/init-defaults'),
};

// Configuration Suivi
export const suiviConfigAPI = {
  // Types d'actions
  getTypes: (includeHidden = false) => api.get(`/suivi-config/types?include_hidden=${includeHidden}`),
  getAllTypes: () => api.get('/suivi-config/types/all'),
  createType: (data) => api.post('/suivi-config/types', data),
  updateType: (id, data) => api.patch(`/suivi-config/types/${id}`, data),
  deleteType: (id) => api.delete(`/suivi-config/types/${id}`),
  initDefaults: () => api.post('/suivi-config/init-defaults'),
  
  // Tâches
  getTaches: (dossierId, statut = null) => {
    let url = `/suivi-config/taches/${dossierId}`;
    if (statut) url += `?statut=${statut}`;
    return api.get(url);
  },
  getTachesUser: (userId, statut = null) => {
    let url = `/suivi-config/taches/user/${userId}`;
    if (statut) url += `?statut=${statut}`;
    return api.get(url);
  },
  createTache: (data) => api.post('/suivi-config/taches', data),
  updateTache: (id, data) => api.patch(`/suivi-config/taches/${id}`, data),
  deleteTache: (id) => api.delete(`/suivi-config/taches/${id}`),
  
  // Suivis enrichis
  getSuivis: (dossierId, filters = {}) => {
    const params = new URLSearchParams();
    if (filters.type_action) params.append('type_action', filters.type_action);
    if (filters.date_debut) params.append('date_debut', filters.date_debut);
    if (filters.date_fin) params.append('date_fin', filters.date_fin);
    if (filters.user_id) params.append('user_id', filters.user_id);
    const queryString = params.toString();
    return api.get(`/suivi-config/suivis/${dossierId}${queryString ? '?' + queryString : ''}`);
  },
  createSuivi: (data) => api.post('/suivi-config/suivis', data),
  
  // Stats
  getStats: (dossierId) => api.get(`/suivi-config/stats/${dossierId}`),
};

// Permissions
export const permissionsAPI = {
  // Référentiels
  getReferentiels: () => api.get('/permissions/referentiels'),
  
  // Profils
  getProfiles: () => api.get('/permissions/profiles'),
  getProfile: (id) => api.get(`/permissions/profiles/${id}`),
  createProfile: (data) => api.post('/permissions/profiles', data),
  updateProfile: (id, data) => api.patch(`/permissions/profiles/${id}`, data),
  deleteProfile: (id) => api.delete(`/permissions/profiles/${id}`),
  
  // Équipes
  getEquipes: () => api.get('/permissions/equipes'),
  getEquipe: (id) => api.get(`/permissions/equipes/${id}`),
  createEquipe: (data) => api.post('/permissions/equipes', data),
  updateEquipe: (id, data) => api.patch(`/permissions/equipes/${id}`, data),
  deleteEquipe: (id) => api.delete(`/permissions/equipes/${id}`),
  
  // Assignations
  getAssignationsDossier: (dossierId) => api.get(`/permissions/assignations/dossier/${dossierId}`),
  getAssignationsGestionnaire: (gestionnaireId) => api.get(`/permissions/assignations/gestionnaire/${gestionnaireId}`),
  createAssignation: (data) => api.post('/permissions/assignations', data),
  deleteAssignation: (id) => api.delete(`/permissions/assignations/${id}`),
  
  // Permissions gestionnaire
  getGestionnairePermissions: (gestionnaireId) => api.get(`/permissions/gestionnaire/${gestionnaireId}`),
  updateGestionnairePermissions: (gestionnaireId, data) => api.patch(`/permissions/gestionnaire/${gestionnaireId}`, data),
  
  // Vérification
  checkPermission: (gestionnaireId, permission) => api.get(`/permissions/check/${gestionnaireId}/${permission}`),
  checkDossierAccess: (gestionnaireId, dossierId) => api.get(`/permissions/check-dossier/${gestionnaireId}/${dossierId}`),
  
  // Audit
  getAuditLogs: (params) => api.get('/permissions/audit', { params }),
  createAuditLog: (data) => api.post('/permissions/audit', data),
  
  // Historique d'activité
  getActivityLogs: (params) => api.get('/permissions/activity', { params }),
  getUserActivity: (userId, limit = 50) => api.get(`/permissions/activity/user/${userId}?limit=${limit}`),
  getActivityStats: () => api.get('/permissions/activity/stats'),
  logActivity: (data) => api.post('/permissions/activity', data),
  
  // Init
  initDefaults: () => api.post('/permissions/init-defaults'),
};

// Dashboard Config
export const dashboardConfigAPI = {
  getWidgets: () => api.get('/config/dashboard/widgets'),
  getVisibleWidgets: () => api.get('/config/dashboard/widgets/visible'),
  getWidget: (code) => api.get(`/config/dashboard/widgets/${code}`),
  updateWidget: (code, data) => api.patch(`/config/dashboard/widgets/${code}`, data),
  toggleWidget: (code) => api.patch(`/config/dashboard/widgets/${code}/toggle`),
  reorderWidgets: (orders) => api.post('/config/dashboard/widgets/reorder', orders),
  createWidget: (data) => api.post('/config/dashboard/widgets', data),
  deleteWidget: (code) => api.delete(`/config/dashboard/widgets/${code}`),
  initDefaults: () => api.post('/config/dashboard/init-defaults'),
  resetWidgets: () => api.post('/config/dashboard/reset'),
  getReferentiels: () => api.get('/config/dashboard/referentiels'),
  // Météo
  getWeather: (city = 'paris') => api.get(`/config/dashboard/weather?city=${city}`),
  getWeatherCities: () => api.get('/config/dashboard/weather/cities'),
};

// Configuration liste dossiers
export const dossiersListConfigAPI = {
  getColumns: () => api.get('/config/dossiers-list'),
  updateColumns: (columns) => api.put('/config/dossiers-list', { columns }),
  resetColumns: () => api.post('/config/dossiers-list/reset'),
  toggleColumn: (code) => api.patch(`/config/dossiers-list/column/${code}/toggle`),
  reorderColumns: (orders) => api.put('/config/dossiers-list/reorder', orders),
};

// Configuration détail dossier
export const detailDossierConfigAPI = {
  getElements: () => api.get('/config/detail-dossier'),
  updateElements: (elements) => api.put('/config/detail-dossier', { elements }),
  resetElements: () => api.post('/config/detail-dossier/reset'),
  toggleElement: (code) => api.patch(`/config/detail-dossier/element/${code}/toggle`),
};

// Formulaire client (public)
export const clientFormAPI = {
  generateToken: (dossierId) => api.get(`/client/generate-token/${dossierId}`),
  getDossierByToken: (token) => api.get(`/client/${token}`),
  submitForm: (token, data) => api.post(`/client/${token}/submit`, data),
  getStatus: (token) => api.get(`/client/${token}/status`),
};

// Configuration lien client
export const clientLinkConfigAPI = {
  getConfig: () => api.get('/config/client-link'),
  updateConfig: (data) => api.put('/config/client-link', data),
  toggle: () => api.patch('/config/client-link/toggle'),
  reset: () => api.post('/config/client-link/reset'),
};

// Lien client documents
export const clientDocumentsAPI = {
  generateToken: (dossierId) => api.get(`/client-documents/generate-token/${dossierId}`),
  getMissingDocuments: (token) => api.get(`/client-documents/${token}`),
  getStatus: (token) => api.get(`/client-documents/${token}/status`),
};

// Configuration documents client (lien client)
export const clientDocumentsConfigAPI = {
  getConfig: () => api.get('/config/client-documents'),
  updateConfig: (data) => api.put('/config/client-documents', data),
  reset: () => api.post('/config/client-documents/reset'),
  sync: () => api.post('/config/client-documents/sync'),
};

// Notifications
export const notificationsAPI = {
  getAll: (params = {}) => api.get('/notifications', { params }),
  getUnreadCount: () => api.get('/notifications/count'),
  markAsRead: (id) => api.post(`/notifications/${id}/read`),
  markAllAsRead: () => api.post('/notifications/read-all'),
  delete: (id) => api.delete(`/notifications/${id}`),
  clearAll: () => api.delete('/notifications/clear-all'),
};

// Comptes Email
export const emailAccountsAPI = {
  getAll: () => api.get('/email-accounts'),
  getPresets: () => api.get('/email-accounts/presets'),
  getDefault: () => api.get('/email-accounts/default'),
  getById: (id) => api.get(`/email-accounts/${id}`),
  create: (data) => api.post('/email-accounts', data),
  update: (id, data) => api.patch(`/email-accounts/${id}`, data),
  delete: (id) => api.delete(`/email-accounts/${id}`),
  testConnection: (data) => api.post('/email-accounts/test-connection', data),
  testAccount: (id) => api.post(`/email-accounts/${id}/test`),
  setDefault: (id) => api.post(`/email-accounts/${id}/set-default`),
};

// Types de travaux
export const travauxAPI = {
  getTypes: (includeHidden = false) => api.get(`/travaux/types?include_hidden=${includeHidden}`),
  createType: (data) => api.post('/travaux/types', data),
  updateType: (id, data) => api.patch(`/travaux/types/${id}`, data),
  deleteType: (id) => api.delete(`/travaux/types/${id}`),
  initDefaults: () => api.post('/travaux/init-defaults'),
};

// Installateurs
export const installateursAPI = {
  getAll: (includeHidden = false) => api.get(`/installateurs?include_hidden=${includeHidden}`),
  create: (data) => api.post('/installateurs', data),
  update: (id, data) => api.patch(`/installateurs/${id}`, data),
  delete: (id) => api.delete(`/installateurs/${id}`),
};

// Statuts d'aide (pour les demandes caisse/département)
export const aideStatutsAPI = {
  getAll: () => api.get('/aide-statuts'),
  getAllWithInactive: () => api.get('/aide-statuts/all'),
  create: (data) => api.post('/aide-statuts', data),
  update: (id, data) => api.put(`/aide-statuts/${id}`, data),
  delete: (id) => api.delete(`/aide-statuts/${id}`),
  reorder: (order) => api.post('/aide-statuts/reorder', order),
};

// Catégories de remarques
export const categoriesRemarquesAPI = {
  getAll: () => api.get('/categories-remarques'),
  getAllWithInactive: () => api.get('/categories-remarques/all'),
  create: (data) => api.post('/categories-remarques', data),
  update: (id, data) => api.put(`/categories-remarques/${id}`, data),
  delete: (id) => api.delete(`/categories-remarques/${id}`),
  reorder: (order) => api.post('/categories-remarques/reorder', order),
};

// Actions rapides (appel, email, courrier, rdv)
export const actionsRapidesAPI = {
  getAll: () => api.get('/actions-rapides'),
  getAllWithInactive: () => api.get('/actions-rapides/all'),
  create: (data) => api.post('/actions-rapides', data),
  update: (id, data) => api.put(`/actions-rapides/${id}`, data),
  delete: (id) => api.delete(`/actions-rapides/${id}`),
  reorder: (order) => api.post('/actions-rapides/reorder', order),
};

// Statuts de progression (barre de progression dossier)
export const statutsProgressionAPI = {
  getAll: () => api.get('/statuts-progression'),
  getAllWithInactive: () => api.get('/statuts-progression/all'),
  create: (data) => api.post('/statuts-progression', data),
  update: (id, data) => api.put(`/statuts-progression/${id}`, data),
  delete: (id) => api.delete(`/statuts-progression/${id}`),
  reorder: (order) => api.post('/statuts-progression/reorder', order),
  resetToDefaults: () => api.post('/statuts-progression/reset-to-defaults'),
};

// Profils utilisateur (admin, senior, gestionnaire, junior)
export const profilsUtilisateurAPI = {
  getAll: () => api.get('/profils-utilisateur'),
  getAllWithInactive: () => api.get('/profils-utilisateur/all'),
  create: (data) => api.post('/profils-utilisateur', data),
  update: (id, data) => api.put(`/profils-utilisateur/${id}`, data),
  delete: (id) => api.delete(`/profils-utilisateur/${id}`),
  reorder: (order) => api.post('/profils-utilisateur/reorder', order),
};

// Statuts des tâches (à faire, en cours, terminé, annulé)
export const statutsTachesAPI = {
  getAll: () => api.get('/statuts-taches'),
  getAllWithInactive: () => api.get('/statuts-taches/all'),
  create: (data) => api.post('/statuts-taches', data),
  update: (id, data) => api.put(`/statuts-taches/${id}`, data),
  delete: (id) => api.delete(`/statuts-taches/${id}`),
  reorder: (order) => api.post('/statuts-taches/reorder', order),
};

// Étapes nouveau dossier (identité, situation, ressources, charges, motif)
export const etapesDossierAPI = {
  getAll: () => api.get('/etapes-dossier'),
  getAllWithInactive: () => api.get('/etapes-dossier/all'),
  create: (data) => api.post('/etapes-dossier', data),
  update: (id, data) => api.put(`/etapes-dossier/${id}`, data),
  delete: (id) => api.delete(`/etapes-dossier/${id}`),
  reorder: (order) => api.post('/etapes-dossier/reorder', order),
};

export default api;
